import { Injectable } from '@angular/core';
import {
    Router,
    CanActivate,
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
    CanLoad,
    UrlTree
} from '@angular/router';
import { Store } from '@ngxs/store';
import { SetUrlForRedirectAfterLogin } from './auth.actions';
import { combineLatest, Observable } from 'rxjs';
import { AuthState } from './auth.state';
import { map, take } from 'rxjs/operators';
import { routeIsValidForNonAuthenticatedUser } from '@oneapp/main/core/utils/oa.utils';
import { MainRoute } from '@oneapp/main/main-routing.models';
import { GamesAndEventingRoute } from '@oneapp/main/games-and-eventing/games-and-eventing-routing.models';
import { PermissionsService } from './permissions.service';
import { OaPermission } from './auth.models';

@Injectable()
export class AuthGuard implements CanActivate, CanLoad {
    private _isLoggedIn$ = this._store.select(AuthState.isLoggedIn);
    private _canLoginOnOneApp$ = this._permissionsService.userHasPermissionInAnySport$(OaPermission.CAN_LOGIN_ON_ONE_APP);

    constructor(
        private _store: Store,
        private _router: Router,
        private _permissionsService: PermissionsService
    ) { }

    public canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> {
        const url = this._router.parseUrl(state.url);

        return combineLatest([
            this._isLoggedIn$,
            this._canLoginOnOneApp$
        ]).pipe(
            // eslint-disable-next-line complexity
            map(([isLoggedIn, canLoginOnOneApp]) => {
                // If the user is already logged, have permission to access OneApp and is accessing the Login page directly,
                // send them back to the "Games" page instead
                if (isLoggedIn && canLoginOnOneApp && url.toString() === '/login') {
                    this._router.navigate([MainRoute.GAMES_AND_EVENTING, GamesAndEventingRoute.GAMES]);
                    return false;
                }

                // User is logged in, doesn't have access to OneApp and tries to access a secured url;
                // return false so that the route is not lazy loaded, and the canLoad guard will redirect to "Login"
                if (isLoggedIn && !canLoginOnOneApp && url.toString() !== '/login') {
                    return false;
                }

                // If the user isn't logged in and trying to access a private page, redirect them
                // to Login page first but keep the target url page for redirection afterward
                if (!isLoggedIn && !routeIsValidForNonAuthenticatedUser(url.toString())) {
                    this._storeUrlForRedirectAfterLogin(url);
                    this._router.navigate(['login']);
                    return false;
                }

                return true;
            })
        );
    }

    public canLoad(): Observable<boolean> {
        return combineLatest([
            this._isLoggedIn$,
            this._canLoginOnOneApp$
        ]).pipe(
            map(([isLoggedIn, canLoginOnOneApp]) => {
                if (!isLoggedIn || !canLoginOnOneApp) {
                    this._router.navigate(['login']);
                }

                return isLoggedIn && canLoginOnOneApp;
            }),
            take(1)
        );
    }

    private _storeUrlForRedirectAfterLogin(url: UrlTree) {
        const segments = url.root.segments;
        const urlForRedirect = segments.reduce((path, currentSegment) => `${path}/${currentSegment.path}`, '');

        if (urlForRedirect && urlForRedirect !== '') {
            this._store.dispatch(new SetUrlForRedirectAfterLogin(urlForRedirect));
        }
    }
}
